import { IdGenerator } from '@inter-ikea-kompis/utilities';
import InsightsReporter from '../reporters/InsightsReporter.js';
/**
 * Used internally in Kompis for managing of {@link IAnalyticsReporter}s which are used to send analytics.
 *
 * Should NOT be used by planners.
 */
class ReporterManager {
    /**
     * Returns an existing connected reporter based on optional id, or creates a new one, or uses the platform reporter set from outside (only in the Kompis platform).
     *
     * @param id The id of an existing reporter, if any. Used to share the same instance of reporter across mutliple trackers.
     */
    static getReporter(id) {
        if (id) {
            const reporter = this.reporterMap.get(id);
            if (!reporter) {
                throw Error(`Expected to find reporter based on id ${id}, but found none.`);
            }
            return reporter;
        }
        return this.platformReporter ?? new InsightsReporter();
    }
    /**
     * Store the reporter instance.
     * This makes it possible to access the same instance of the reporter across multiple components by passing an id to the reporter between components.
     *
     * @param reporter The reporter.
     */
    static connect(reporter) {
        const id = this.idGenerator.generateId();
        this.reporterMap.set(id, reporter);
    }
    /**
     * Attempts to remove the provided reporter from the reporter cache.
     * This will only have an effect if the reporter has been connected earlier.
     *
     * @param reporter The reporter.
     */
    static disconnect(reporter) {
        const id = this.getConnectedId(reporter);
        if (id) {
            this.reporterMap.delete(id);
        }
    }
    /**
     * Returns whether the reporter has been connected or not.
     *
     * @param reporter The reporter.
     */
    static isConnected(reporter) {
        return !!this.getConnectedId(reporter);
    }
    /**
     * Get the id of the provided reporter.
     * The id can only be found if the reporter is connected.
     *
     * @param reporter The reporter.
     */
    static getConnectedId(reporter) {
        for (const [id, reporterEntry] of this.reporterMap.entries()) {
            if (reporterEntry === reporter) {
                return id;
            }
        }
        return null;
    }
    /**
     * Used internally to make tracking in the platform work. Is always PlatformReporter.
     *
     * @param platformReporter ...
     */
    static setPlatformReporter(platformReporter) {
        this.platformReporter = platformReporter;
    }
}
ReporterManager.platformReporter = null;
ReporterManager.reporterMap = new Map();
ReporterManager.idGenerator = new IdGenerator();
export default ReporterManager;
//# sourceMappingURL=ReporterManager.js.map